import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Cliente {
  id: number;
  nome: string;
  whatsapp?: string | null;
}

interface Veiculo {
  id: number;
  cliente_id: number | null;
  cliente_nome?: string | null;
  placa: string;
  tipo: string;
  marca: string | null;
  modelo: string | null;
  ano: number | null;
  cor: string | null;
  chassi: string | null;
  renavam: string | null;
  km_atual: number | null;
  observacoes: string | null;
  status: string;
}

const TIPOS = [
  ['carro', 'Carro'],
  ['moto', 'Moto'],
  ['caminhao', 'Caminhao'],
  ['van', 'Van'],
  ['outro', 'Outro'],
];

function erroMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function placa(v: string): string {
  return v.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 10);
}

function vazioParaNull(v?: string | number | null): string | number | null {
  return v === '' || v === undefined ? null : v;
}

export default function Veiculos() {
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [form, setForm] = useState<Partial<Veiculo>>({ tipo: 'carro', status: 'ativo' });
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [salvando, setSalvando] = useState(false);
  const editando = Boolean(form.id);

  async function carregar() {
    setErro('');
    const [vs, cs] = await Promise.all([
      api<Veiculo[]>('/cadastros/veiculos.php'),
      api<Cliente[]>('/cadastros/clientes.php?somente_ativos=1'),
    ]);
    setVeiculos(vs);
    setClientes(cs);
  }

  useEffect(() => {
    carregar().catch((e) => setErro(erroMsg(e)));
  }, []);

  function limpar() {
    setForm({ tipo: 'carro', status: 'ativo' });
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setSalvando(true);
    const body = {
      id: form.id,
      cliente_id: form.cliente_id || null,
      placa: placa(form.placa || ''),
      tipo: form.tipo || 'carro',
      marca: vazioParaNull(form.marca),
      modelo: vazioParaNull(form.modelo),
      ano: form.ano || null,
      cor: vazioParaNull(form.cor),
      chassi: vazioParaNull(form.chassi),
      renavam: vazioParaNull(form.renavam),
      km_atual: form.km_atual || null,
      observacoes: vazioParaNull(form.observacoes),
      status: form.status || 'ativo',
    };

    try {
      if (form.id) await api('/cadastros/veiculos.php', { method: 'PUT', body });
      else await api('/cadastros/veiculos.php', { method: 'POST', body });
      setMsg(editando ? 'Veiculo atualizado.' : 'Veiculo criado.');
      limpar();
      await carregar();
    } catch (e2) {
      setErro(erroMsg(e2));
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(v: Veiculo) {
    setErro('');
    setMsg('');
    try {
      await api('/cadastros/veiculos.php?id=' + v.id, { method: 'DELETE' });
      setMsg('Veiculo excluido.');
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    }
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>Veiculos</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(erroMsg(e)))}>Atualizar</button>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <div className="grid cols2">
        <form className="card" onSubmit={salvar}>
          <h3>{editando ? 'Editar veiculo' : 'Novo veiculo'}</h3>
          <div className="grid cols2">
            <div>
              <label>Placa *</label>
              <input className="input" value={form.placa || ''} onChange={(e) => setForm({ ...form, placa: placa(e.target.value) })} required autoFocus />
            </div>
            <div>
              <label>Tipo</label>
              <select value={form.tipo || 'carro'} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          <label>Dono do veiculo</label>
          <select value={form.cliente_id || ''} onChange={(e) => setForm({ ...form, cliente_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Sem cliente vinculado</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.whatsapp ? ' - ' + c.whatsapp : ''}</option>)}
          </select>

          <div className="grid cols2">
            <div><label>Marca</label><input className="input" value={form.marca || ''} onChange={(e) => setForm({ ...form, marca: e.target.value })} /></div>
            <div><label>Modelo</label><input className="input" value={form.modelo || ''} onChange={(e) => setForm({ ...form, modelo: e.target.value })} /></div>
            <div><label>Ano</label><input className="input" type="number" min={0} value={form.ano || ''} onChange={(e) => setForm({ ...form, ano: e.target.value ? Number(e.target.value) : null })} /></div>
            <div><label>Cor</label><input className="input" value={form.cor || ''} onChange={(e) => setForm({ ...form, cor: e.target.value })} /></div>
            <div><label>KM atual</label><input className="input" type="number" min={0} value={form.km_atual || ''} onChange={(e) => setForm({ ...form, km_atual: e.target.value ? Number(e.target.value) : null })} /></div>
            <div>
              <label>Status</label>
              <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
          </div>

          <div className="grid cols2">
            <div><label>Chassi</label><input className="input" value={form.chassi || ''} onChange={(e) => setForm({ ...form, chassi: e.target.value })} /></div>
            <div><label>Renavam</label><input className="input" value={form.renavam || ''} onChange={(e) => setForm({ ...form, renavam: e.target.value })} /></div>
          </div>

          <label>Observacoes</label>
          <textarea className="input" rows={3} value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />

          <div className="form-actions">
            {editando && <button type="button" className="btn sec" onClick={limpar}>Cancelar</button>}
            <button className="btn" disabled={salvando}>{salvando ? 'Salvando...' : (editando ? 'Salvar veiculo' : 'Adicionar veiculo')}</button>
          </div>
        </form>

        <div className="tabela-wrap">
          <table>
            <thead><tr><th>Placa</th><th>Veiculo</th><th>Cliente</th><th>KM</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {veiculos.map((v) => (
                <tr key={v.id}>
                  <td><strong style={{ fontFamily: 'monospace' }}>{v.placa}</strong></td>
                  <td>{[v.marca, v.modelo, v.ano].filter(Boolean).join(' ') || '-'}</td>
                  <td>{v.cliente_nome || '-'}</td>
                  <td>{v.km_atual ?? '-'}</td>
                  <td><span className="badge">{v.status}</span></td>
                  <td className="acoes">
                    <button className="btn sec sm" onClick={() => setForm(v)}>Editar</button>
                    <button className="btn danger sm" onClick={() => excluir(v)}>Excluir</button>
                  </td>
                </tr>
              ))}
              {!veiculos.length && <tr><td colSpan={6} className="vazio">Nenhum veiculo cadastrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
